import { ThemeProvider, useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { RouterProvider } from 'react-router-dom';
import { router } from './navigation/router';
import './App.css';
import { Snackbar } from './foundation/notifications/snackbar';
import { Guard } from './features/auth/guard';
import { Header } from './navigation/header';
import { WindowProvider } from './window/window.provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTrpcClient, trpc, wsConnectionStatus$ } from './trpc';
import React, { useEffect, useMemo, useState } from 'react';
import { useAddExchangeFormEvents } from './features/settings/api-management/add-exchange-form-events.hook';
import { useTradingStatsEvents } from './widgets/chart/trading-stats/trading-stats-events.hook';
import 'flexlayout-react/style/light.css';
import './flexlayout.css';
import { useFileDownloadFormEvents } from './widgets/file-download/file-download-form-events.hook';
import { isExcludedRoute } from './features/auth/excluded-routes';
import { usePath } from './navigation/use-path';
import { WidgetProvider } from './widgets/widget.hook';
import { WindowStateProvider } from './window/window.hook';
import { appTheme } from './foundation/design-system/theme';
import { colorTokens } from './foundation/design-system/tokens';
import { useObservable } from './foundation/shared/utils/observable.hook';
import { useDialogWindowEvents } from './foundation/shared/components/dialog-window-events.hook';
import { useIndicatorTemplateEditorEvents } from './features/builders/indicator-template-editor-events.hook';
import { useStrategyTemplateEditorEvents } from './features/builders/strategy-template-editor-events.hook';
import { useSignalBlockTemplateEditorEvents } from './features/builders/signal-block-template-editor-events.hook';

const useWidgets = () => {
  useFileDownloadFormEvents();
  useAddExchangeFormEvents();
  useTradingStatsEvents();
  useDialogWindowEvents();
  useIndicatorTemplateEditorEvents();
  useStrategyTemplateEditorEvents();
  useSignalBlockTemplateEditorEvents();
};

const App = () => {
  useWidgets();

  return (
    <Guard>
      <Routes />
    </Guard>
  );
};

const AppProvider = () => {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() => createTrpcClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={appTheme}>
          <CssBaseline />
          <WidgetProvider>
            <WindowStateProvider>
              <Header />
              <WindowProvider />
              <App />
              <Snackbar />
            </WindowStateProvider>
          </WidgetProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
};

const Routes = () => {
  const theme = useTheme();
  const { path } = usePath();
  const isAuthRoute = isExcludedRoute(path);
  const isWorkspaceRoute = path === '/live-trading' || path === '/backtesting';
  const wsConnectionStatus = useObservable(
    () => wsConnectionStatus$,
    'connecting'
  );
  const [showConnectingBanner, setShowConnectingBanner] = useState(false);

  useEffect(() => {
    if (wsConnectionStatus !== 'connecting') {
      setShowConnectingBanner(false);
      return;
    }

    // Prevent banner flash on fast initial connect.
    const timeoutId = window.setTimeout(() => {
      setShowConnectingBanner(true);
    }, 750);

    return () => window.clearTimeout(timeoutId);
  }, [wsConnectionStatus]);

  const showConnectionBanner =
    isWorkspaceRoute &&
    (wsConnectionStatus === 'reconnecting' ||
      (wsConnectionStatus === 'connecting' && showConnectingBanner));
  const connectionLabel =
    wsConnectionStatus === 'connecting' ? 'Connecting...' : 'Reconnecting...';

  const cssVars = useMemo(() => {
    return {
      '--mui-grey-400': theme.palette.grey[400],
      '--mui-grey-800': theme.palette.grey[800],
      '--mui-primary-main': theme.palette.primary.main,
      '--ds-button-primary-gradient': theme.designSystem.button.primaryGradient,
      '--ds-button-primary-gradient-hover':
        theme.designSystem.button.primaryGradientHover,
      '--ds-button-primary-border': theme.designSystem.button.primaryBorder,
      '--ds-button-primary-border-hover':
        theme.designSystem.button.primaryBorderHover,
      '--ds-button-primary-text': theme.designSystem.button.primaryText,
      '--ds-button-secondary-bg': theme.designSystem.button.outlinedBackground,
      '--ds-button-secondary-bg-hover':
        theme.designSystem.button.outlinedBackgroundHover,
      '--ds-button-secondary-gradient': `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackground}, ${theme.designSystem.button.outlinedBackgroundHover})`,
      '--ds-button-secondary-gradient-hover': `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackgroundHover}, ${theme.designSystem.button.outlinedBackground})`,
      '--ds-button-secondary-border': theme.designSystem.button.outlinedBorder,
      '--ds-button-secondary-border-hover':
        theme.designSystem.button.outlinedBorderHover,
      '--ds-button-secondary-text': theme.designSystem.button.outlinedText
    } as React.CSSProperties;
  }, [
    theme.designSystem.button.outlinedBackground,
    theme.designSystem.button.outlinedBackgroundHover,
    theme.designSystem.button.outlinedBorder,
    theme.designSystem.button.outlinedBorderHover,
    theme.designSystem.button.outlinedText,
    theme.designSystem.button.primaryBorder,
    theme.designSystem.button.primaryBorderHover,
    theme.designSystem.button.primaryGradient,
    theme.designSystem.button.primaryGradientHover,
    theme.designSystem.button.primaryText,
    theme.palette.grey,
    theme.palette.primary.main
  ]);

  return (
    <Box
      sx={{
        padding: isAuthRoute ? 0 : isWorkspaceRoute ? 0 : 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colorTokens.slate950Alpha95
      }}
      style={cssVars}
    >
      {showConnectionBanner ? (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            mb: 1,
            width: '100%',
            mx: 'auto',
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.2,
            color: theme.palette.error.light,
            background:
              'linear-gradient(160deg, rgba(82, 22, 29, 0.9), rgba(58, 12, 18, 0.94))',
            border: '1px solid rgba(244, 67, 54, 0.45)',
            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.28)'
          }}
        >
          {connectionLabel}
        </Box>
      ) : null}
      <RouterProvider router={router} />
    </Box>
  );
};

export default AppProvider;
